import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Share as SocialIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const MarketingCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState('month');

  // Create form state
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventType, setNewEventType] = useState('Email');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTime, setNewEventTime] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventAudience, setNewEventAudience] = useState('');
  const [newEventChannel, setNewEventChannel] = useState('');

  // Sample marketing events
  const marketingEvents = [
    {
      id: 1,
      title: 'Summer Sale Email Campaign',
      type: 'Email',
      date: '2024-01-22',
      time: '10:00 AM',
      status: 'Scheduled',
      description: 'Send summer furniture collection promotion to all subscribers',
      audience: 'All Subscribers',
      channel: 'Email Marketing',
    },
    {
      id: 2,
      title: 'Instagram Product Showcase',
      type: 'Social Media',
      date: '2024-01-22',
      time: '2:00 PM',
      status: 'Scheduled',
      description: 'Post new modern sofa collection on Instagram',
      audience: 'Instagram Followers',
      channel: 'Social Media',
    },
    {
      id: 3,
      title: 'Google Ads Campaign Launch',
      type: 'Campaign',
      date: '2024-01-23',
      time: '9:00 AM',
      status: 'Scheduled',
      description: 'Launch new customer acquisition campaign',
      audience: 'Target Demographics',
      channel: 'Google Ads',
    },
    {
      id: 4,
      title: 'Newsletter - Weekly Deals',
      type: 'Email',
      date: '2024-01-24',
      time: '11:00 AM',
      status: 'Draft',
      description: 'Weekly newsletter with special deals and new arrivals',
      audience: 'Newsletter Subscribers',
      channel: 'Email Marketing',
    },
    {
      id: 5,
      title: 'Facebook Ad Campaign Review',
      type: 'Campaign',
      date: '2024-01-25',
      time: '3:00 PM',
      status: 'Scheduled',
      description: 'Review and optimize Facebook advertising performance',
      audience: 'Facebook Users',
      channel: 'Social Media',
    },
    {
      id: 6,
      title: 'Customer Survey Launch',
      type: 'Email',
      date: '2024-01-26',
      time: '1:00 PM',
      status: 'Scheduled',
      description: 'Send customer satisfaction survey to recent buyers',
      audience: 'Recent Customers',
      channel: 'Email Marketing',
    },
  ];

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'Email':
        return 'primary';
      case 'Social Media':
        return 'secondary';
      case 'Campaign':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'Email':
        return <EmailIcon />;
      case 'Social Media':
        return <SocialIcon />;
      case 'Campaign':
        return <CampaignIcon />;
      default:
        return <EventIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'success';
      case 'Draft':
        return 'warning';
      case 'Completed':
        return 'info';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return marketingEvents.filter(event => event.date === dateStr);
  };

  const getTodaysEvents = () => {
    const today = new Date().toISOString().split('T')[0];
    return marketingEvents.filter(event => event.date === today);
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    return marketingEvents
      .filter(event => new Date(event.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Prefill from localStorage draft set by other pages (e.g., Field Marketing)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('calendarDraftEvent');
      if (raw) {
        const draft = JSON.parse(raw);
        setNewEventTitle(draft.title || '');
        setNewEventType(draft.type || 'Email');
        setNewEventDate(draft.date || '');
        setNewEventTime(draft.time || '');
        setNewEventDescription(draft.description || '');
        setNewEventAudience(draft.audience || '');
        setNewEventChannel(draft.channel || '');
        setCreateOpen(true);
        localStorage.removeItem('calendarDraftEvent');
      }
    } catch (_) {
      // ignore JSON errors
    }
  }, []);

  const handleOpenCreate = () => {
    setNewEventTitle('');
    setNewEventType('Email');
    setNewEventDate('');
    setNewEventTime('');
    setNewEventDescription('');
    setNewEventAudience('');
    setNewEventChannel('');
    setCreateOpen(true);
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Marketing Calendar
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ fontWeight: 'bold' }}
          >
            Add Event
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Calendar View */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* Calendar Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Typography>
                <Box>
                  <IconButton onClick={() => navigateMonth(-1)}>
                    <PrevIcon />
                  </IconButton>
                  <IconButton onClick={() => navigateMonth(1)}>
                    <NextIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Calendar Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <Box key={day} sx={{ p: 1, textAlign: 'center', fontWeight: 'bold', color: 'text.secondary' }}>
                    {day}
                  </Box>
                ))}
                
                {/* Calendar days */}
                {generateCalendarDays().map((date, index) => {
                  if (!date) {
                    return <Box key={index} sx={{ p: 1, minHeight: 80 }} />;
                  }
                  
                  const dayEvents = getEventsForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1,
                        minHeight: 80,
                        backgroundColor: isToday ? 'primary.light' : 'transparent',
                        opacity: isToday ? 0.1 : 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: isToday ? 'bold' : 'normal' }}>
                        {date.getDate()}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {dayEvents.slice(0, 2).map((event, eventIndex) => (
                          <Chip
                            key={eventIndex}
                            label={event.title}
                            size="small"
                            color={getEventTypeColor(event.type)}
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 16, 
                              mb: 0.5, 
                              display: 'block',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleEventClick(event)}
                          />
                        ))}
                        {dayEvents.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{dayEvents.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Today's Events */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Today's Events
              </Typography>
              {getTodaysEvents().length > 0 ? (
                <List>
                  {getTodaysEvents().map((event) => (
                    <ListItem
                      key={event.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleEventClick(event)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }} color={getEventTypeColor(event.type)}>
                          {getEventTypeIcon(event.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={event.title}
                        secondary={`${event.time} - ${event.type}`}
                      />
                      <Chip
                        label={event.status}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No events scheduled for today
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Upcoming Events
              </Typography>
              <List>
                {getUpcomingEvents().map((event, index) => (
                  <React.Fragment key={event.id}>
                    <ListItem
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleEventClick(event)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }} color={getEventTypeColor(event.type)}>
                          {getEventTypeIcon(event.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={event.title}
                        secondary={`${event.date} at ${event.time}`}
                      />
                      <Chip
                        label={event.status}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                    </ListItem>
                    {index < getUpcomingEvents().length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Event Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Event Details</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedEvent.title}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  icon={getEventTypeIcon(selectedEvent.type)}
                  label={selectedEvent.type}
                  color={getEventTypeColor(selectedEvent.type)}
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={selectedEvent.status}
                  color={getStatusColor(selectedEvent.status)}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {selectedEvent.date} at {selectedEvent.time}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedEvent.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Channel:</strong> {selectedEvent.channel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Audience:</strong> {selectedEvent.audience}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit Event</Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Event Title"
            margin="normal"
            value={newEventTitle}
            onChange={(e) => setNewEventTitle(e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event Type</InputLabel>
            <Select label="Event Type" value={newEventType} onChange={(e) => setNewEventType(e.target.value)}>
              <MenuItem value="Email">Email Campaign</MenuItem>
              <MenuItem value="Social Media">Social Media Post</MenuItem>
              <MenuItem value="Campaign">Ad Campaign</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Date"
            type="date"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newEventDate}
            onChange={(e) => setNewEventDate(e.target.value)}
          />
          <TextField
            fullWidth
            label="Time"
            type="time"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newEventTime}
            onChange={(e) => setNewEventTime(e.target.value)}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            margin="normal"
            value={newEventDescription}
            onChange={(e) => setNewEventDescription(e.target.value)}
          />
          <TextField
            fullWidth
            label="Target Audience"
            margin="normal"
            value={newEventAudience}
            onChange={(e) => setNewEventAudience(e.target.value)}
          />
          <TextField
            fullWidth
            label="Channel"
            margin="normal"
            value={newEventChannel}
            onChange={(e) => setNewEventChannel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Event</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketingCalendar;
